/** Page (relative to the add-in root) rendered inside the create-deal dialog. */
export const CREATE_DEAL_DIALOG_PAGE = "create-deal-dialog.html";

/** Message the dialog page sends via messageParent to ask the task pane to close it. */
export const CREATE_DEAL_DIALOG_CLOSE_MESSAGE = "triyo-create-deal-dialog-close";

/** displayDialogAsync error code raised when the add-in already has a dialog open. */
const DIALOG_ALREADY_OPEN_CODE = 12007;

/** Dialog size in percent of the Office host window (~the prototype's 560px dialog). */
const DIALOG_SIZE = { height: 70, width: 35 } as const;

export type CreateDealDisplay = "pane" | "dialog";

/**
 * Resolves the configured "Create Deal" surface (`createDealDisplay` in
 * public/config.js). Defaults to "dialog" — the prototype presents deal
 * creation as an Office dialog over Outlook — unless "pane" is configured.
 */
export function getCreateDealDisplay(): CreateDealDisplay {
  return window.APP_SETTINGS?.createDealDisplay === "pane" ? "pane" : "dialog";
}

let activeDialog: Office.Dialog | null = null;

function closeActiveDialog(): void {
  activeDialog?.close();
  activeDialog = null;
}

/**
 * Opens the create-deal page in an Office dialog over the Outlook window, or a
 * popup window outside Office (browser preview). Resolves "opened" while a
 * dialog is showing — including one that was already open — and "unavailable"
 * when nothing could be opened, in which case callers should fall back to the
 * inline task-pane wizard.
 */
export function openCreateDealDialog(): Promise<"opened" | "unavailable"> {
  const url = new URL(CREATE_DEAL_DIALOG_PAGE, window.location.href).toString();

  if (typeof Office !== "undefined" && Office.context?.ui?.displayDialogAsync) {
    return new Promise((resolve) => {
      Office.context.ui.displayDialogAsync(
        url,
        { height: DIALOG_SIZE.height, width: DIALOG_SIZE.width, displayInIframe: true },
        (result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            resolve(result.error?.code === DIALOG_ALREADY_OPEN_CODE ? "opened" : "unavailable");
            return;
          }
          activeDialog = result.value;
          result.value.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg: { message: string; origin: string | undefined } | { error: number }) => {
              if ("message" in arg && arg.message === CREATE_DEAL_DIALOG_CLOSE_MESSAGE) {
                closeActiveDialog();
              }
            }
          );
          result.value.addEventHandler(Office.EventType.DialogEventReceived, () => {
            activeDialog = null;
          });
          resolve("opened");
        }
      );
    });
  }

  // "noopener" must stay out of the features string — with it, window.open
  // returns null even for a successfully opened popup.
  const popup = window.open(url, "_blank", "popup=yes,width=600,height=760");
  return Promise.resolve(popup ? "opened" : "unavailable");
}

/**
 * Called from inside the dialog page: asks the hosting task pane to close this
 * dialog, falling back to window.close() outside Office (browser preview).
 */
export function requestCreateDealDialogClose(): void {
  if (typeof Office !== "undefined" && Office.context?.ui?.messageParent) {
    Office.context.ui.messageParent(CREATE_DEAL_DIALOG_CLOSE_MESSAGE);
    return;
  }
  window.close();
}
